/**
 * State 5 of 9 — data delayed (spec 01 §18, `PRODUCT.md` §4).
 *
 * **Driven by**, and the three sources are kept apart because three different
 * people fix them:
 *
 *   source='achau'       `AchauSyncState.last_status` ∈ {`failed`, `partial`} or a
 *                        `last_success_at` older than the sweep interval, from
 *                        `POST /api/v1/admin/sync`; and, on the timer screens,
 *                        `TaskSearchResponse.degraded === true` or
 *                        `cache_status` ∈ {`stale`, `never_synced`}. This is spec 01
 *                        §18's "task data delayed khi achau down", named.
 *   source='processing'  `AttendanceDay.completeness.unsynced_data === true` — the
 *                        day has been calculated but events behind it are still
 *                        landing (Task 23's processing lag).
 *   source='agent'       heartbeats are arriving but the events they promise are
 *                        not, so the device is *not* offline and `AgentOffline`
 *                        would be the wrong state.
 *
 * `openapi.yaml` says the same thing about `degraded` and `cache_status`: merging
 * them "would leave a caller unable to tell 'achau is down' from 'the sweep is
 * late' — two situations needing different actions from different people." This
 * component keeps that distinction on screen.
 *
 * **An unlabelled stale number is worse than no number** (`PRODUCT.md` §4), so the
 * screen must render this *next to the figure it qualifies*, not once at the top of
 * the page.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../i18n';
import { formatInstantWithAge } from '../i18n/datetime';
import type { MessageKey } from '../i18n/messages';
import { StatusNote } from './StatusNote';

export type DelayedSource = 'agent' | 'achau' | 'processing';

const BODY_KEY: Readonly<Record<DelayedSource, MessageKey>> = {
  agent: 'state.dataDelayed.agent',
  achau: 'state.dataDelayed.achau',
  processing: 'state.dataDelayed.processing',
};

export interface DataDelayedProps {
  readonly source: DelayedSource;
  /** The last time this source was known good. `null` means it never has been. */
  readonly since: string | null;
  /** The company time zone from `SessionUser.timezone`. Required — see `i18n/datetime.ts`. */
  readonly timezone: string;
  /** Anything screen-specific: which report, which department. */
  readonly detail?: string;
  readonly actions?: ReactNode;
  readonly testId?: string;
}

export function DataDelayed({
  source,
  since,
  timezone,
  detail,
  actions,
  testId = 'state-data-delayed',
}: DataDelayedProps): ReactNode {
  const { t, locale } = useI18n();
  return (
    <StatusNote
      severity="medium"
      title={t('state.dataDelayed.title')}
      testId={testId}
      actions={actions}
    >
      <p>{t(BODY_KEY[source])}</p>
      {detail === undefined ? null : <p className="wp-muted">{detail}</p>}
      <p className="wp-small wp-muted">
        {since === null
          ? t('state.dataDelayed.sinceNever')
          : t('state.dataDelayed.since', {
              when: formatInstantWithAge(since, locale, timezone, t('common.never')),
            })}
      </p>
    </StatusNote>
  );
}
